import {
  BrowserOptions,
  browserTracingIntegration,
  createReduxEnhancer,
  init,
  replayIntegration,
  setUser,
  withProfiler,
} from "@sentry/react";

// Ignore some Error types to prevent cluttering Sentry with bad errors.
// Inspired by https://gist.github.com/impressiver/5092952
export const sentryIgnoreErrors: BrowserOptions["ignoreErrors"] = [
  // ResizeObserverLoops
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
  // Random plugins/extensions
  "top.GLOBALS",
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "http://tt.epicplay.com",
  "Can't find variable: ZiteReader",
  "jigsaw is not defined",
  "ComboSearch is not defined",
  "http://loading.retry.widdit.com/",
  "atomicFindClose",
  // Facebook borked
  "fb_xd_fragment",
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  "bmi_SafeAddOnload",
  "EBCallBackMessageReceived",
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  "conduitPage",
  // Generic error code from errors outside the security sandbox
  // You can delete this if using raven.js > 1.0, which ignores these automatically.
  "Script error.",
  // Avast extension error
  "_avast_submit",
  // Firefox reader mode + native bridge
  /__firefox__/,
  // Native-bridge unhandledrejection string (dropped more precisely in beforeSend)
  /Object Not Found Matching Id/,
];

export const sentryDenyUrls: BrowserOptions["denyUrls"] = [
  // Google Adsense
  /pagead\/js/i,
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  /fbapp/i,
  // Extension-injected ServiceWorker registrar
  /wrsParams/i,
  // Firefox reader mode
  /\/reader\//i,
  // Headway widget
  /headwayapp\.co\/widget\.js/i,
];

/**
 * Why: Centralizes all beforeSend drop decisions so they can be unit-tested without mocking @sentry/react.
 * What: Returns null to drop the event, or the event unchanged to keep it.
 * Test: Assert FB/IG UA drops, native-bridge unhandledrejection drops, normal events pass.
 */
export const filterSentryEvent: BrowserOptions["beforeSend"] = (event) => {
  const ua = navigator.userAgent || "";
  if (/FBAN|FBAV|Instagram/.test(ua)) return null;

  // Native-bridge unhandledrejection with a Non-Error payload (iOS/Android webview bridge)
  const firstException = event.exception?.values?.[0];
  if (
    firstException?.mechanism?.type === "onunhandledrejection" &&
    /Object Not Found Matching Id/.test(firstException?.value || "")
  ) {
    return null;
  }

  return event;
};

export const initSentry = (tracesSampleRate = 0.02) => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    // Parse the DSN and get the tunnel URL
    const {host: sentryHost, pathname} = new URL(process.env.REACT_APP_SENTRY_DSN);
    const sentryHostId = sentryHost.split(".")[0];
    const sentryProjectId = pathname.slice(1);

    const endpoint =
      process.env.NODE_ENV === "production"
        ? `${process.env.REACT_APP_API_URL}/proxy`
        : "http://localhost:4010";

    const proxyTunnelUrl = `${endpoint}/all-bugs-on-me?sentryHostId=${sentryHostId}&sentryProjectId=${sentryProjectId}`;

    init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
      release: process.env.REACT_APP_GIT_VERSION || undefined,
      tunnel: proxyTunnelUrl,

      // Sentry Tracing: performance and links between front an back transactions
      tracesSampleRate,

      // Sentry replay feature: replay stuff when there is an error
      replaysOnErrorSampleRate: 1.0,

      // Add soe depth for the Redux state to be recorded
      normalizeDepth: 10,

      integrations: [browserTracingIntegration(), replayIntegration()],

      ignoreErrors: sentryIgnoreErrors,
      denyUrls: sentryDenyUrls,

      // Filter errors from app browsers like Facebook and Instagram
      beforeSend: filterSentryEvent,
    });
  }
};

export const getSentryReduxEnhancer = () =>
  createReduxEnhancer({
    stateTransformer: (state) => {
      // If we do it like that, it's because there is a reason.... :)

      try {
        // Transform the state to remove sensitive information
        let transformedState = {...state};

        if (transformedState.currentUser?.user)
          transformedState = Object.assign({}, transformedState, {
            currentUser: {
              ...transformedState.currentUser,
              user: {...transformedState.currentUser.user, xsrfToken: null},
            },
          });

        if (transformedState.currentProject?.project) {
          if (transformedState.currentProject.project.helloAsso)
            transformedState = Object.assign({}, transformedState, {
              currentProject: {
                ...transformedState.currentProject,
                project: {
                  ...transformedState.currentProject.project,
                  helloAsso: null,
                },
              },
            });
        }

        let transformedStateWithoutBigListData: any = {...transformedState};
        for (const slice of Object.keys(transformedState)) {
          if (transformedState[slice]?.entities) {
            transformedStateWithoutBigListData[slice] = {
              ...transformedStateWithoutBigListData[slice],
              ids: transformedStateWithoutBigListData[slice].ids?.length,
              entities: null, // remove list data
              list: null, // remove list data
              slotList: null, // remove list data
            };
          }
        }

        return transformedStateWithoutBigListData;
      } catch (e) {
        console.error(
          "Error when transforming Redux state for Sentry. Sending without currentUser and currentProjet.",
          e
        );
        return {message: "Error when transforming Redux state for Sentry.", error: e?.toString?.()};
      }
    },
  });

export const changeSentryUser = (user?: {email: string; _id: string}) =>
  setUser(user?.email ? {email: user.email, id: user._id} : null);

export const withSentryProfiler = withProfiler;
